import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function ComparisonSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const rawItems = content.items || [];

    // Detect format: table mode (columns as strings + items with feature/values) vs card mode (items with title/features)
    const isTableFormat = Array.isArray(content.columns) && rawItems.length > 0 && rawItems[0]?.feature;

    const defaultItems = [
        { title: 'Opcion A', features: 'Caracteristica principal\nSoporte incluido\nActualizaciones gratis', highlighted: false },
        { title: 'Opcion B', features: 'Todo lo de Opcion A\nFunciones avanzadas\nSoporte prioritario\nPersonalizacion', highlighted: true },
    ];
    const items = !isTableFormat && rawItems.length > 0 ? rawItems : (!isTableFormat ? defaultItems : rawItems);

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // Table format: render as comparison table
    if (isTableFormat) {
        const tableCols = content.columns.map(c => typeof c === 'string' ? { label: c } : c);
        const tableRows = rawItems;
        const borderColor = s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="mt-8 overflow-x-auto">
                        <table className="w-full text-sm" style={{ color: s.text }}>
                            <thead>
                                <tr style={{ borderBottom: `2px solid ${borderColor}` }}>
                                    <th className="text-left py-3 pr-4 font-semibold" style={{ color: s.textSub }}>{content.feature_label || 'Caracteristica'}</th>
                                    {tableCols.map((col, i) => (
                                        <th key={i} className="py-3 px-4 text-center font-bold" style={{ color: i === 0 ? s.accent : s.text }}>
                                            {col.label || col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableRows.map((row, ri) => (
                                    <tr key={ri} style={{ borderBottom: `1px solid ${s.dark ? 'rgba(255,255,255,0.05)' : '#f3f4f6'}` }}
                                        className={ri % 2 === 0 ? (s.dark ? 'bg-white/[0.02]' : 'bg-gray-50/50') : ''}>
                                        <td className="py-3 pr-4 font-medium" style={{ color: s.text }}>{row.feature}</td>
                                        {(row.values || []).map((val, vi) => (
                                            <td key={vi} className="py-3 px-4 text-center" style={{ color: s.textSub }}>
                                                {val === 'Si' || val === 'yes' || val === true ? (
                                                    <svg className="w-5 h-5 mx-auto" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                                                ) : val === 'No' || val === 'no' || val === false ? (
                                                    <span className={s.dark ? 'text-white/25' : 'text-gray-300'}>✕</span>
                                                ) : val}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // Card format: parse features from each column (newline-separated)
    const columns = items.map(col => ({
        ...col,
        featureList: (col.features || '').split('\n').map(f => f.trim()).filter(Boolean),
    }));

    // Collect all unique features for row comparison
    const allFeatures = [...new Set(columns.flatMap(c => c.featureList))];

    const cols = columns.length <= 2 ? 'md:grid-cols-2' : columns.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4';

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                {/* Cards layout */}
                <div className={`grid grid-cols-1 ${cols} gap-6`}>
                    {columns.map((col, i) => (
                        <div key={i} className={`${card} p-6 transition-all duration-500 ${col.highlighted ? 'ring-2' : ''} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{
                                transitionDelay: visible ? `${i * 120}ms` : '0ms',
                                ...(col.highlighted ? { ringColor: s.accent, borderColor: s.accent } : {}),
                            }}>
                            {col.highlighted && (
                                <div className="text-[10px] uppercase tracking-wider font-bold text-center py-1 -mt-6 -mx-6 mb-4 rounded-t-2xl text-white"
                                    style={{ backgroundColor: s.accent }}>
                                    {content.recommended_label || 'Recomendado'}
                                </div>
                            )}
                            <h3 className="text-xl font-bold text-center mb-4" style={{ color: s.text }}>{col.title}</h3>
                            {col.subtitle && <p className="text-sm text-center mb-4" style={{ color: s.textSub }}>{col.subtitle}</p>}
                            <div className="space-y-3">
                                {col.featureList.map((feat, fi) => (
                                    <div key={fi} className="flex items-start gap-2.5">
                                        <svg className="w-5 h-5 shrink-0 mt-0.5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="text-sm" style={{ color: s.text }}>{feat}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Table layout for 2+ columns (hidden on mobile) */}
                {columns.length >= 2 && allFeatures.length > 0 && (
                    <div className="hidden lg:block mt-12 overflow-x-auto">
                        <table className="w-full text-sm" style={{ color: s.text }}>
                            <thead>
                                <tr style={{ borderBottom: `2px solid ${s.dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb'}` }}>
                                    <th className="text-left py-3 pr-4 font-semibold" style={{ color: s.textSub }}>{content.feature_label || 'Caracteristica'}</th>
                                    {columns.map((col, i) => (
                                        <th key={i} className="py-3 px-4 text-center font-bold" style={{ color: col.highlighted ? s.accent : s.text }}>
                                            {col.title}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {allFeatures.map((feat, fi) => (
                                    <tr key={fi} style={{ borderBottom: `1px solid ${s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6'}` }}>
                                        <td className="py-2.5 pr-4" style={{ color: s.textSub }}>{feat}</td>
                                        {columns.map((col, ci) => (
                                            <td key={ci} className="py-2.5 px-4 text-center">
                                                {col.featureList.includes(feat) ? (
                                                    <svg className="w-5 h-5 mx-auto" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                ) : (
                                                    <span style={{ color: s.dark ? 'rgba(255,255,255,0.25)' : '#d1d5db' }}>--</span>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
